import React, { useMemo } from 'react';

import { BearingEnvelope } from '@paragon/contract-library';
import { assertExhaustiveSwitch } from '@paragon/utilities';

import { useAppSelector } from '../../../components/hooks';
import { getSelectedItemIdentifiers } from '../../../library/selection/selectors';
import { isBearingEnvelopeSelectableType } from '../../../library/selection/types';
import {
  getVisibleBearingEnvelopesWithActiveEdit,
  shouldInteractiveEditingBeDisabled,
} from '../../../store/selectors/interactiveEditingSelectors';
import {
  is3DLayoutCameraInUse,
  shouldShowBearingEnvelopeDimensions,
} from '../../../store/selectors/layoutModeSelectors';
import {
  getActiveBeamMode,
  getActiveNonStructuralWallMode,
  getActiveRenderModeForBearingEnvelope,
  getActiveWallMode,
} from '../../../store/selectors/layoutVisualOptionsSelectors';
import { createGuidSet } from '../../../store/selectors/util/createGuidSet';
import { createSimpleSetSelector } from '../../../store/selectors/util/createSimpleSetSelector';
import { OptimizedDistanceDimensionContainer } from '../../dimension/OptimizedDistanceDimension';
import { useAutoInvalidateOnChange } from '../../hooks/invalidation-hooks';
import { Segments } from '../Segments';
import { createAutomaticElevationHatchingMap } from './automaticHatching';
import { BearingEnvelopeDimension } from './BearingEnvelopeDimension';
import { BearingEnvelopeInsertionController } from './BearingEnvelopeInsertionController';
import { BearingEnvelopeIntersectionEdges } from './BearingEnvelopeIntersectionEdges';
import { BearingEnvelopeVisual } from './BearingEnvelopeVisual';
import { HatchingMode } from './generateEnvelopeHatchLines';

const getSelectedEnvelopeGuids = createSimpleSetSelector(getSelectedItemIdentifiers, (items) =>
  createGuidSet(items.filter((item) => isBearingEnvelopeSelectableType(item.type))),
);

export function BearingEnvelopesController() {
  const threeDimensional = useAppSelector(is3DLayoutCameraInUse);

  const bearingEnvelopes = useAppSelector(getVisibleBearingEnvelopesWithActiveEdit);
  const selectedEnvelopeGuids = useAppSelector(getSelectedEnvelopeGuids);
  const interactiveEditingDisabled = useAppSelector(shouldInteractiveEditingBeDisabled);

  const structuralWallMode = useAppSelector(getActiveWallMode);
  const nonStructuralWallMode = useAppSelector(getActiveNonStructuralWallMode);
  const beamMode = useAppSelector(getActiveBeamMode);
  const showBearings = structuralWallMode !== 'none' || nonStructuralWallMode !== 'none' || beamMode !== 'none';
  const { visible: showDimensions } = useAppSelector(shouldShowBearingEnvelopeDimensions);

  useAutoInvalidateOnChange(structuralWallMode, nonStructuralWallMode, beamMode, bearingEnvelopes);

  const wallHatchingMap = useMemo(
    () =>
      createAutomaticElevationHatchingMap(bearingEnvelopes.filter((e) => e.bearingType === 'Wall').map((e) => e.top)),
    [bearingEnvelopes],
  );

  const beamHatchingMap = useMemo(
    () =>
      createAutomaticElevationHatchingMap(bearingEnvelopes.filter((e) => e.bearingType === 'Beam').map((e) => e.top)),
    [bearingEnvelopes],
  );

  const solidBearingEnvelopes = useMemo(
    () =>
      bearingEnvelopes.filter((envelope) => {
        const mode = getActiveRenderModeForBearingEnvelope(
          envelope,
          structuralWallMode,
          nonStructuralWallMode,
          beamMode,
        );
        return mode === 'solid';
      }),
    [bearingEnvelopes, nonStructuralWallMode, structuralWallMode, beamMode],
  );

  function getHatchingModeForEnvelope(envelope: BearingEnvelope): HatchingMode | undefined {
    switch (envelope.bearingType) {
      case 'Beam':
        return beamHatchingMap.get(envelope.top);

      case 'Wall':
        return wallHatchingMap.get(envelope.top);

      default:
        assertExhaustiveSwitch(envelope.bearingType);
    }
  }

  if (showBearings === false) return null;

  function isEnvelopeVisible(envelope: BearingEnvelope): boolean {
    const mode = getActiveRenderModeForBearingEnvelope(envelope, structuralWallMode, nonStructuralWallMode, beamMode);
    return mode !== 'none' || selectedEnvelopeGuids.has(envelope.guid);
  }

  const bearingsToDimension = showDimensions
    ? bearingEnvelopes.filter(isEnvelopeVisible)
    : selectedEnvelopeGuids.size > 0
      ? bearingEnvelopes.filter((envelope) => selectedEnvelopeGuids.has(envelope.guid))
      : [];

  const selectedEnvelopes = bearingEnvelopes.filter((envelope) => selectedEnvelopeGuids.has(envelope.guid));
  const nonSelectedEnvelopes = bearingEnvelopes.filter((envelope) => !selectedEnvelopeGuids.has(envelope.guid));

  const someEnvelopesAreSelected = selectedEnvelopeGuids.size > 0;

  return (
    <>
      <Segments initialSize={100} sizeIncrement={100} lineWidth={threeDimensional ? 2.0 : 1.0}>
        {nonSelectedEnvelopes.map((envelope) => (
          <BearingEnvelopeVisual
            key={envelope.guid}
            style={someEnvelopesAreSelected ? 'faded' : 'normal'}
            threeDimensional={threeDimensional}
            envelope={envelope}
            editInteractionsEnabled={false}
            hatchingMode={getHatchingModeForEnvelope(envelope)}
          />
        ))}

        <BearingEnvelopeInsertionController threeDimensional={threeDimensional} />

        {threeDimensional && <BearingEnvelopeIntersectionEdges envelopes={solidBearingEnvelopes} />}
      </Segments>

      {/*
        rendering selected envelopes separately ensures they will always win the depth test over co-linear segments
        from unselected envelopes.
      */}
      <Segments initialSize={100} sizeIncrement={100} lineWidth={threeDimensional ? 2.0 : 1.0}>
        {selectedEnvelopes.map((envelope) => (
          <BearingEnvelopeVisual
            key={envelope.guid}
            style="emphasised"
            threeDimensional={threeDimensional}
            envelope={envelope}
            editInteractionsEnabled={!interactiveEditingDisabled}
            hatchingMode={getHatchingModeForEnvelope(envelope)}
          />
        ))}
      </Segments>

      <OptimizedDistanceDimensionContainer threeDimensional={threeDimensional}>
        {bearingsToDimension.map((envelope) => (
          <BearingEnvelopeDimension key={envelope.guid} envelope={envelope} />
        ))}
      </OptimizedDistanceDimensionContainer>
    </>
  );
}
